import type { Shape } from '../types';
import type { CategoryDef, MeasGeometryContext } from '../../../../helpers/api/config/config';
import {
  categoryHasPairedLengthAndPerimeter,
  getEffectiveMaxStructuresForSave,
  getLengthMeasForCategory,
  getMaxStructuresFromApi,
  getPerimeterMeasForCategory,
  measurementInstanceLimit,
} from '../../../../helpers/api/config/measConfig';
import { getMeasurementNameFromLabel } from '../../../../helpers/api/config/config';
import { getMeasurementDisplayName } from '../../../../constants/measurementLabels';
import {
  calculateEllipseArea,
  calculateEllipsePerimeter,
  calculateLineLength,
  calculatePolylineLength,
} from './shapeUtils';

const LEGACY_MEAS_GEOMETRY: MeasGeometryContext = {
  perimeterMeasLabels: new Set(['MEAS__HC', 'MEAS__AC']),
  lengthToPerimeter: { MEAS__BPD: 'MEAS__HC', MEAS__ATD: 'MEAS__AC' },
  perimeterToLength: { MEAS__HC: 'MEAS__BPD', MEAS__AC: 'MEAS__ATD' },
};

let activeMeasGeometry: MeasGeometryContext = LEGACY_MEAS_GEOMETRY;
export function setActiveMeasGeometry(ctx: MeasGeometryContext | null | undefined): void {
  activeMeasGeometry =
    ctx && ctx.perimeterMeasLabels.size > 0 ? ctx : LEGACY_MEAS_GEOMETRY;
}

export type ShapeForSave = Shape & {
  backendLabel?: string | null;
  backendType?: string | null;
  categoryId?: number | null;
};

function getMeasTagForShapeType(shapeType: string): string | null {
  const t = (shapeType || '').toLowerCase();
  if (!t) return null;
  if (t === 'ruler') return 'meas_ruler';
  if (t === 'line' || t === 'polyline') return 'meas_line';
  if (t === 'circle') return 'meas_circle';
  if (t === 'square') return 'meas_rect';
  return 'meas_freehand';
}

function shapeUsesAnonymousMeasTool(shape: ShapeForSave): boolean {
  return (
    shape.categoryId == null &&
    ['line', 'ruler', 'circle', 'freehand', 'square', 'polygon'].includes(shape.type ?? '')
  );
}

/**
 * Resolve the label that will be persisted for a canvas shape.
 * Matches General Viewer behaviour: categories with meas_name (FL, AC, SDP, …)
 * always save as MEAS__* even when the display label is a human name like "Femur".
 */
export function resolveShapeSaveLabel(
  shape: ShapeForSave,
  categoryIdToMeasLabel: Record<number, string> = {},
  labelToMeasName: Record<string, string> = {},
  categories: CategoryDef[] = [],
  measCtx: MeasGeometryContext = LEGACY_MEAS_GEOMETRY
): string | null {
  const geometryType = getShapeGeometryType(shape);

  const backendLabelRaw = (shape.backendLabel ?? '').toString().trim();
  if (backendLabelRaw.startsWith('MEAS__')) {
    const key = backendLabelRaw.split(/\s+/)[0];
    return (
      correctMeasLabelForGeometry(key, shape.categoryId, geometryType, {
        length: (shape as any).length,
        perimeter: (shape as any).perimeter,
      }, measCtx) ?? key
    );
  }

  const measLabelFromCategory =
    shape.categoryId != null && categoryIdToMeasLabel[shape.categoryId]
      ? categoryIdToMeasLabel[shape.categoryId]
      : null;

  if (shape.categoryId != null && measLabelFromCategory) {
    const picked = pickMeasFromCategoryAndGeometry(measLabelFromCategory, geometryType, {
      length: (shape as any).length,
      perimeter: (shape as any).perimeter,
    }, measCtx);
    if (
      picked &&
      isMeasurableGeometry(geometryType, shape.type) &&
      (categoryMeasAppliesToShape(picked, geometryType, shape.type, measCtx) ||
        reconcileMeasLabelWithGeometry(measLabelFromCategory, geometryType, {
          length: (shape as any).length,
          perimeter: (shape as any).perimeter,
        }, measCtx) === picked)
    ) {
      return picked;
    }
  }

  if (shape.categoryId != null && categories.length) {
    const cat = categories.find((c) => c.id === shape.categoryId);
    if (categoryHasPairedLengthAndPerimeter(cat)) {
      const fam = normalizeGeometryFamily(geometryType, shape.type);
      if (fam === 'ellipse') return getPerimeterMeasForCategory(cat);
      if (fam === 'line') return getLengthMeasForCategory(cat);
    }
  }

  // Categories with meas_name: only the matching tool geometry becomes biometry
  // (line for diameter/length, ellipse for AC/HC perimeter).
  if (
    measLabelFromCategory &&
    isMeasurableGeometry(geometryType, shape.type) &&
    categoryMeasAppliesToShape(measLabelFromCategory, geometryType, shape.type, measCtx)
  ) {
    return measLabelFromCategory;
  }

  const rawLabel = (shape.label || shape.backendLabel || '').toString();
  const measName =
    getMeasurementNameFromLabel(rawLabel, labelToMeasName) ||
    (rawLabel.startsWith('MEAS__')
      ? rawLabel.trim().split(/\s+/)[0] ?? rawLabel
      : null);

  const tagForTool =
    !measName && shapeUsesAnonymousMeasTool(shape)
      ? getMeasTagForShapeType(shape.type ?? '')
      : null;

  const customSlugRaw =
    shape.label && typeof shape.label === 'string'
      ? shape.label.trim().toLowerCase().replace(/\s+/g, '_')
      : null;
  const customSlug = customSlugRaw ? customSlugRaw.replace(/^(meas_)+/i, '') : null;
  const measurementLabel = customSlug ? `meas_${customSlug}` : tagForTool;

  if (shape.categoryId != null) {
    if (
      measName &&
      categoryMeasAppliesToShape(measName, geometryType, shape.type, measCtx)
    ) {
      return correctMeasLabelForGeometry(measName, shape.categoryId, geometryType, undefined, measCtx) ?? measName;
    }
    if (
      measLabelFromCategory &&
      isMeasurableGeometry(geometryType, shape.type) &&
      categoryMeasAppliesToShape(measLabelFromCategory, geometryType, shape.type, measCtx)
    ) {
      return measLabelFromCategory;
    }
    return null;
  }

  return measName ?? measurementLabel ?? null;
}

export function resolveShapeSegmentType(
  label: string | null,
  shape: ShapeForSave,
  labelToMeasName: Record<string, string> = {}
): 'structure' | 'measurement' {
  if (label?.startsWith('MEAS__')) return 'measurement';
  // A categorized freehand/polygon outline is the STRUCTURE, even when its category
  // owns a caliper measurement (e.g. amniotic_fluid → MEAS__SDP). That caliper is a
  // separate derived line; saving the contour itself as the measurement makes it
  // round-trip back as a MEAS__ shape and breaks the structure↔line link.
  if (
    (shape.type === 'freehand' || shape.type === 'square') &&
    shape.categoryId != null
  ) {
    return 'structure';
  }
  const rawLabel = (shape.label || shape.backendLabel || '').toString();
  const measName = getMeasurementNameFromLabel(rawLabel, labelToMeasName);
  const tagForTool =
    !measName && shapeUsesAnonymousMeasTool(shape)
      ? getMeasTagForShapeType(shape.type ?? '')
      : null;
  return measName || tagForTool ? 'measurement' : 'structure';
}

type FrameSegmentation = {
  id?: number | string;
  category_id?: number | null;
  points?: string | null;
  label?: string | null;
  geometry_type?: string | null;
  segment_type?: string | null;
  is_manual?: boolean;
  display_name?: string | null;
  perimeter?: number | null;
  length?: number | null;
  origin_id?: string | null;
};

export type StructureConflictGroup = {
  conflictKey: string;
  category_id: number;
  slotKey: string;
  displayName: string;
  userSegs: FrameSegmentation[];
  mlSegs: FrameSegmentation[];
};

function formatConflictDisplayName(
  seg: FrameSegmentation | undefined,
  slotKey: string,
  categoryId: number
): string {
  const label = (seg?.label ?? '').toString().trim();
  if (label.startsWith('MEAS__')) {
    return label.split(/\s+/)[0].replace('MEAS__', '').replace(/_/g, ' ');
  }
  if (seg?.display_name) return seg.display_name;
  if (slotKey.startsWith('MEAS__')) {
    return slotKey.split(':')[0].replace('MEAS__', '').replace(/_/g, ' ');
  }
  return `Category ${categoryId}`;
}

function conflictPointCoords(points: string | null | undefined): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  if (!points || typeof points !== 'string') return out;
  for (const raw of points.split(',')) {
    const token = raw.trim();
    if (!token) continue;
    const sep = token.includes('\\') ? '\\' : token.includes('//') ? '//' : null;
    if (!sep) continue;
    const [xs, ys] = token.split(sep);
    const x = Number(xs);
    const y = Number(ys);
    if (Number.isFinite(x) && Number.isFinite(y)) out.push([x, y]);
  }
  return out;
}

function conflictFootprint(
  seg: FrameSegmentation
): { cx: number; cy: number; radius: number } | null {
  const coords = conflictPointCoords(seg.points);
  if (coords.length === 0) return null;
  const xs = coords.map((c) => c[0]);
  const ys = coords.map((c) => c[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return {
    cx: (minX + maxX) / 2,
    cy: (minY + maxY) / 2,
    radius: Math.hypot(maxX - minX, maxY - minY) / 2,
  };
}


function pairSameStructure(
  userSegs: FrameSegmentation[],
  mlSegs: FrameSegmentation[]
): Array<{ user: FrameSegmentation; ml: FrameSegmentation }> {
  const candidates: Array<{ d: number; u: number; m: number }> = [];
  userSegs.forEach((user, u) => {
    const uf = conflictFootprint(user);
    mlSegs.forEach((ml, m) => {
      const mf = conflictFootprint(ml);
      if (!uf || !mf) {
        candidates.push({ d: Number.MAX_SAFE_INTEGER, u, m });
        return;
      }
      const distance = Math.hypot(uf.cx - mf.cx, uf.cy - mf.cy);
      if (distance > 0.5 * (uf.radius + mf.radius)) return;
      candidates.push({ d: distance, u, m });
    });
  });
  candidates.sort((a, b) => a.d - b.d || a.u - b.u || a.m - b.m);

  const takenUser = new Set<number>();
  const takenMl = new Set<number>();
  const pairs: Array<{ user: FrameSegmentation; ml: FrameSegmentation }> = [];
  for (const { u, m } of candidates) {
    if (takenUser.has(u) || takenMl.has(m)) continue;
    takenUser.add(u);
    takenMl.add(m);
    pairs.push({ user: userSegs[u], ml: mlSegs[m] });
  }
  return pairs;
}

/**
 * Group user-vs-AI conflicts by biometry slot (e.g. BPD line vs HC ellipse on fetal brain),
 * not by category alone — so resolving BPD does not delete AI head circumference.
 *
 * For a category that may hold several instances (two femurs, two kidneys...), the slot alone
 * is not enough: both femurs' MEAS__FL rows share one slot, so hand-editing one caliper made
 * the other femur's AI caliper look like a disagreement about the same structure, and
 * "Use AI only" then deleted the wrong femur's drawing. Pass `multiInstanceCategoryIds` and
 * those categories are matched per instance instead — one conflict per structure that both
 * sides actually drew, each resolvable on its own.
 */
export function groupStructureConflictsBySlot(
  segmentations: FrameSegmentation[],
  categoryIdToMeasLabel: Record<number, string> = {},
  measLabelToCategoryId: Record<string, number> = {},
  multiInstanceCategoryIds: ReadonlySet<number> = new Set()
): StructureConflictGroup[] {
  const bySlot = new Map<
    string,
    {
      category_id: number;
      slotKey: string;
      userSegs: FrameSegmentation[];
      mlSegs: FrameSegmentation[];
    }
  >();

  for (const seg of segmentations) {
    const catId = resolveSegmentationCategoryId(seg, measLabelToCategoryId);
    if (catId == null) continue;

    const slotKey = getBiometrySlotKey(seg, catId, categoryIdToMeasLabel);
    const conflictKey = `${catId}::${slotKey}`;
    if (!bySlot.has(conflictKey)) {
      bySlot.set(conflictKey, {
        category_id: catId,
        slotKey,
        userSegs: [],
        mlSegs: [],
      });
    }
    const bucket = bySlot.get(conflictKey)!;
    if (seg.is_manual) bucket.userSegs.push(seg);
    else bucket.mlSegs.push(seg);
  }

  const conflicts: StructureConflictGroup[] = [];
  for (const [conflictKey, bucket] of bySlot) {
    if (bucket.userSegs.length === 0 || bucket.mlSegs.length === 0) continue;
    const baseName = formatConflictDisplayName(
      bucket.userSegs[0] ?? bucket.mlSegs[0],
      bucket.slotKey,
      bucket.category_id
    );

    // Single-instance category: the whole slot is one structure, so a user drawing and an AI
    // drawing of it disagree however far apart they sit.
    if (!multiInstanceCategoryIds.has(bucket.category_id)) {
      conflicts.push({
        conflictKey,
        category_id: bucket.category_id,
        slotKey: bucket.slotKey,
        displayName: baseName,
        userSegs: bucket.userSegs,
        mlSegs: bucket.mlSegs,
      });
      continue;
    }

    const pairs = pairSameStructure(bucket.userSegs, bucket.mlSegs);
    pairs.forEach((pair, index) => {
      conflicts.push({
        conflictKey: `${conflictKey}::${pair.user.id ?? index}`,
        category_id: bucket.category_id,
        slotKey: bucket.slotKey,
        displayName:
          pairs.length > 1 ? `${baseName} ${index + 1}` : baseName,
        userSegs: [pair.user],
        mlSegs: [pair.ml],
      });
    });
  }

  return conflicts;
}

function normalizePoints(points: string | null | undefined): string {
  return (points ?? '').trim();
}

function getMeasLabelFromSeg(seg: {
  label?: string | null;
}): string | null {
  const label = (seg.label ?? '').toString().trim();
  if (label.startsWith('MEAS__')) return label.split(/\s+/)[0];
  return null;
}

function getMeasLabelFromShape(shape: {
  backendLabel?: string | null;
  label?: string | null;
}): string | null {
  const raw = (shape.backendLabel ?? shape.label ?? '').toString().trim();
  if (raw.startsWith('MEAS__')) return raw.split(/\s+/)[0];
  return null;
}

/** MEAS__* labels whose value is ellipse perimeter — prefer DB via measCtx. */
export function getPerimeterMeasLabels(
  measCtx: MeasGeometryContext = LEGACY_MEAS_GEOMETRY
): Set<string> {
  return measCtx.perimeterMeasLabels;
}

/** @deprecated Use measGeometryContext from CategoriesContext */
export const PERIMETER_MEAS_LABELS = LEGACY_MEAS_GEOMETRY.perimeterMeasLabels;

/** @deprecated Use measGeometryContext from CategoriesContext */
export const MEAS_LENGTH_TO_PERIMETER = LEGACY_MEAS_GEOMETRY.lengthToPerimeter;
export const MEAS_PERIMETER_TO_LENGTH = LEGACY_MEAS_GEOMETRY.perimeterToLength;

type GeometryFamily = 'line' | 'ellipse' | 'freehand' | 'polygon' | 'unknown';

type SegMetrics = { perimeter?: number | null; length?: number | null };

function normalizeGeometryFamily(
  geometryType?: string | null,
  shapeType?: string | null
): GeometryFamily {
  const gt = (geometryType ?? '').toString().trim().toUpperCase();
  const st = (shapeType ?? '').toString().trim().toLowerCase();
  if (gt === 'ELLIPSE' || gt === 'CIRCLE' || st === 'circle') return 'ellipse';
  if (gt === 'POLYLINE' || gt === 'LINE' || st === 'line' || st === 'ruler') return 'line';
  if (gt === 'POLYGON' || st === 'square' || st === 'polygon') return 'polygon';
  if (gt === 'FREEHAND' || st === 'freehand') return 'freehand';
  return 'unknown';
}

export function isPerimeterDominantSegment(seg: SegMetrics): boolean {
  const per = seg.perimeter != null ? Number(seg.perimeter) : null;
  const len = seg.length != null ? Number(seg.length) : null;
  if (per == null || Number.isNaN(per)) return false;
  return len == null || Number.isNaN(len) || per > len * 1.5;
}

function geometryWantsPerimeterMeas(
  geometryType?: string | null,
  seg?: SegMetrics
): boolean {
  return (
    normalizeGeometryFamily(geometryType, null) === 'ellipse' ||
    Boolean(seg && isPerimeterDominantSegment(seg))
  );
}

function geometryWantsLengthMeas(
  geometryType?: string | null,
  seg?: SegMetrics
): boolean {
  if (normalizeGeometryFamily(geometryType, null) === 'line') return true;
  if (seg?.length != null && !isPerimeterDominantSegment(seg)) return true;
  return false;
}

/** Align meas label with geometry + segment metrics (length vs perimeter). */
export function reconcileMeasLabelWithGeometry(
  measLabel: string,
  geometryType?: string | null,
  seg?: SegMetrics,
  measCtx: MeasGeometryContext = LEGACY_MEAS_GEOMETRY
): string {
  if (!measLabel.startsWith('MEAS__')) return measLabel;
  const key = measLabel.split(/\s+/)[0];
  const isPerimeterMeas = measCtx.perimeterMeasLabels.has(key);
  const wantsPerim = geometryWantsPerimeterMeas(geometryType, seg);
  const wantsLen = geometryWantsLengthMeas(geometryType, seg);

  if (!isPerimeterMeas && wantsPerim && !wantsLen) {
    return measCtx.lengthToPerimeter[key] ?? key;
  }
  if (isPerimeterMeas && wantsLen && !wantsPerim) {
    return measCtx.perimeterToLength[key] ?? key;
  }
  return key;
}

function pickMeasFromCategoryAndGeometry(
  catMeas: string,
  geometryType: string,
  seg: SegMetrics,
  measCtx: MeasGeometryContext = LEGACY_MEAS_GEOMETRY
): string | null {
  if (categoryMeasAppliesToShape(catMeas, geometryType, null, measCtx)) {
    return catMeas;
  }
  return reconcileMeasLabelWithGeometry(catMeas, geometryType, seg, measCtx);
}

/** Perimeter biometry uses ellipse; length/diameter biometry uses line. */
export function categoryMeasAppliesToShape(
  measLabel: string,
  geometryType: string,
  shapeType?: string | null,
  measCtx: MeasGeometryContext = LEGACY_MEAS_GEOMETRY
): boolean {
  if (measCtx.perimeterMeasLabels.has(measLabel)) {
    return normalizeGeometryFamily(geometryType, shapeType) === 'ellipse';
  }
  return normalizeGeometryFamily(geometryType, shapeType) === 'line';
}

/** Fix swapped length/perimeter labels when geometry and meas name disagree. */
export function correctMeasLabelForGeometry(
  measLabel: string | null | undefined,
  _categoryId: number | null | undefined,
  geometryType?: string | null,
  seg?: SegMetrics,
  measCtx: MeasGeometryContext = LEGACY_MEAS_GEOMETRY
): string | null {
  if (!measLabel?.startsWith('MEAS__')) return measLabel ?? null;
  return reconcileMeasLabelWithGeometry(measLabel.split(/\s+/)[0], geometryType, seg, measCtx);
}

/** Resolve MEAS__* for a segmentation using category meas_name + geometry + metrics. */
export function resolveMeasLabelForSegmentation(
  seg: FrameSegmentation,
  categoryIdToMeasLabel: Record<number, string> = {},
  measLabelToCategoryId: Record<string, number> = {},
  measCtx: MeasGeometryContext = LEGACY_MEAS_GEOMETRY
): string | null {
  const categoryId = resolveSegmentationCategoryId(seg, measLabelToCategoryId);
  const geo = (seg.geometry_type ?? '').toString().trim().toUpperCase();
  const rawLabel = (seg.label ?? '').toString().trim();
  const catMeas =
    categoryId != null ? categoryIdToMeasLabel[categoryId] ?? null : null;
  const isMeas = isMeasurementSegmentation(seg);
  const metrics: SegMetrics = { perimeter: seg.perimeter, length: seg.length };

  let meas: string | null = rawLabel.startsWith('MEAS__')
    ? rawLabel.split(/\s+/)[0]
    : null;

  if (!meas && isMeas && catMeas) {
    meas = pickMeasFromCategoryAndGeometry(catMeas, geo, metrics, measCtx);
  }

  if (meas && catMeas) {
    meas = reconcileMeasLabelWithGeometry(meas, geo, metrics, measCtx);
  } else if (!meas && catMeas && isMeas) {
    meas = pickMeasFromCategoryAndGeometry(catMeas, geo, metrics, measCtx);
  }

  return meas;
}

/** @deprecated Use resolveMeasLabelForSegmentation */
export function inferMeasLabelForSegmentation(
  seg: FrameSegmentation,
  categoryId: number,
  categoryIdToMeasLabel: Record<number, string>
): string | null {
  return resolveMeasLabelForSegmentation(
    { ...seg, category_id: seg.category_id ?? categoryId },
    categoryIdToMeasLabel,
    {}
  );
}

function isMeasurementSegmentation(seg: FrameSegmentation): boolean {
  const label = (seg.label ?? '').toString().trim();
  if (label.startsWith('MEAS__')) return true;
  return (seg.segment_type ?? '').toString().toLowerCase() === 'measurement';
}

export type FrameMeasurementRow = {
  id?: string | number;
  meas_name: string;
  value?: number;
  unit?: string;
};

/** Convert stored segmentation length/perimeter (px) to cm when pixel spacing is known. */
export function computeMeasurementValueCmFromSeg(
  seg: FrameSegmentation,
  measLabel: string,
  pixelSpacing?: { x: number; y: number } | null,
  measCtx: MeasGeometryContext = LEGACY_MEAS_GEOMETRY
): number | null {
  const scale = pixelSpacing
    ? (pixelSpacing.x + (pixelSpacing.y ?? pixelSpacing.x)) / 2
    : null;
  const toCm = (px: number) => (scale ? (px * scale) / 10 : px);

  if (measCtx.perimeterMeasLabels.has(measLabel)) {
    if (seg.perimeter != null) return toCm(Number(seg.perimeter));
  }
  if (seg.length != null) return toCm(Number(seg.length));
  if (measCtx.perimeterMeasLabels.has(measLabel) && seg.perimeter != null) {
    return toCm(Number(seg.perimeter));
  }
  return null;
}

function findMeasurementForSegment(
  seg: FrameSegmentation,
  measLabel: string | null,
  frameMeasurements: FrameMeasurementRow[]
): FrameMeasurementRow | undefined {
  if (!measLabel || !frameMeasurements.length) return undefined;
  const segId = seg.id != null ? String(seg.id) : null;
  if (segId) {
    const exact = frameMeasurements.find(
      (m) => String(m.id) === segId && m.meas_name === measLabel
    );
    if (exact) return exact;
    const byId = frameMeasurements.find((m) => String(m.id) === segId);
    if (byId) return byId;
  }
  return frameMeasurements.find((m) => m.meas_name === measLabel);
}

/** Attach resolved meas label + panel-aligned value for canvas overlay. */
export function enrichShapeWithMeasurementOverlay(
  shape: Record<string, unknown>,
  seg: FrameSegmentation,
  frameMeasurements: FrameMeasurementRow[] = [],
  pixelSpacing?: { x: number; y: number } | null,
  categoryIdToMeasLabel: Record<number, string> = {},
  measLabelToCategoryId: Record<string, number> = {},
  measCtx: MeasGeometryContext = LEGACY_MEAS_GEOMETRY,
  pixelSpacingUnit?: string,
  categoryIdToMeasNames: Record<number, string[]> = {}
): Record<string, unknown> {
  const resolved = resolveSegmentationLabelsForLoad(
    seg,
    categoryIdToMeasLabel,
    measLabelToCategoryId,
    undefined,
    measCtx
  );
  const backendLabel = resolved.backendLabel;
  const matched = findMeasurementForSegment(seg, backendLabel, frameMeasurements);

  const catId = resolveSegmentationCategoryId(seg, measLabelToCategoryId);
  const perimeterMeasForCat =
    catId != null
      ? (categoryIdToMeasNames[catId] ?? []).find((n) =>
          measCtx.perimeterMeasLabels.has(n)
        ) ?? null
      : null;

  const geometryPerimeterMeas =
    backendLabel && measCtx.perimeterMeasLabels.has(backendLabel)
      ? backendLabel
      : perimeterMeasForCat;

  let calculatedValue: number | undefined;
  let calculatedValueUnit = matched?.unit ?? (pixelSpacing ? 'cm' : 'px');

  const lengthMeasForCat =
    catId != null
      ? (categoryIdToMeasNames[catId] ?? []).find(
          (n) =>
            n.startsWith('MEAS__') && !measCtx.perimeterMeasLabels.has(n)
        ) ?? null
      : null;

  const geometryLengthMeas =
    backendLabel &&
    backendLabel.startsWith('MEAS__') &&
    !measCtx.perimeterMeasLabels.has(backendLabel)
      ? backendLabel
      : lengthMeasForCat;

  if (
    geometryPerimeterMeas &&
    shape.type === 'circle' &&
    typeof shape.radiusX === 'number' &&
    typeof shape.radiusY === 'number'
  ) {
    calculatedValue = calculateEllipsePerimeter(
      shape.radiusX,
      shape.radiusY,
      pixelSpacing ?? undefined,
      pixelSpacingUnit
    );
    calculatedValueUnit = pixelSpacing ? 'cm' : 'px';
  } else if (
    geometryLengthMeas &&
    shape.type === 'line' &&
    shape.startPoint &&
    shape.endPoint
  ) {
    calculatedValue = calculateLineLength(
      shape.startPoint as { x: number; y: number },
      shape.endPoint as { x: number; y: number },
      pixelSpacing ?? undefined
    );
    calculatedValueUnit = pixelSpacing ? 'cm' : 'px';
  } else {
    calculatedValue = matched?.value;
    if (calculatedValue == null && backendLabel?.startsWith('MEAS__')) {
      calculatedValue =
        computeMeasurementValueCmFromSeg(seg, backendLabel, pixelSpacing, measCtx) ??
        undefined;
      calculatedValueUnit = pixelSpacing ? 'cm' : 'px';
    }
  }

  const overlayMeasLabel =
    geometryPerimeterMeas ?? geometryLengthMeas ?? backendLabel;
  const showOverlayValue = Boolean(
    overlayMeasLabel?.startsWith('MEAS__') && calculatedValue != null
  );

  return {
    ...shape,
    label: resolved.displayLabel,
    backendLabel: backendLabel ?? geometryPerimeterMeas,
    segmentType: resolved.segmentType,
    calculatedValue,
    calculatedValueUnit,
    showOverlayValue,
  };
}

/** Resolve backendLabel + canvas display label when loading segmentations from API. */
export function resolveSegmentationLabelsForLoad(
  seg: FrameSegmentation,
  categoryIdToMeasLabel: Record<number, string> = {},
  measLabelToCategoryId: Record<string, number> = {},
  structureDisplayName?: string | null,
  measCtx: MeasGeometryContext = LEGACY_MEAS_GEOMETRY
): {
  backendLabel: string | null;
  displayLabel: string;
  segmentType: 'structure' | 'measurement';
} {
  const rawLabel = (seg.label ?? '').toString().trim();
  const isMeas = isMeasurementSegmentation(seg);
  const backendLabel = resolveMeasLabelForSegmentation(
    seg,
    categoryIdToMeasLabel,
    measLabelToCategoryId,
    measCtx
  );

  const segmentType: 'structure' | 'measurement' =
    backendLabel?.startsWith('MEAS__') || isMeas ? 'measurement' : 'structure';

  let displayLabel: string;
  if (backendLabel?.startsWith('MEAS__')) {
    displayLabel = getMeasurementDisplayName(backendLabel);
  } else if (seg.display_name) {
    displayLabel = seg.display_name;
  } else if (structureDisplayName) {
    displayLabel = structureDisplayName;
  } else if (rawLabel && !rawLabel.startsWith('MEAS__')) {
    displayLabel = rawLabel;
  } else {
    const categoryId = resolveSegmentationCategoryId(seg, measLabelToCategoryId);
    displayLabel =
      categoryId != null ? `Category ${categoryId}` : 'Structure';
  }

  return { backendLabel, displayLabel, segmentType };
}

function isMeasurableGeometry(
  geometryType: string,
  shapeType?: string | null
): boolean {
  return (
    geometryType === 'ELLIPSE' ||
    geometryType === 'CIRCLE' ||
    geometryType === 'POLYLINE' ||
    geometryType === 'LINE' ||
    shapeType === 'line' ||
    shapeType === 'ruler' ||
    shapeType === 'circle'
  );
}

/** Geometry types a drawn shape can honestly be stored as. */
const GEOMETRIES_FOR_SHAPE_TYPE: Record<string, ReadonlySet<string>> = {
  line: new Set(['LINE', 'POLYLINE', 'RULER']),
  ruler: new Set(['LINE', 'POLYLINE', 'RULER']),
  circle: new Set(['ELLIPSE', 'CIRCLE']),
  square: new Set(['POLYGON', 'RECT', 'RECTANGLE', 'SQUARE']),
};

function getShapeGeometryType(shape: ShapeForSave): string {
  const normalize = (value: unknown) =>
    String(value ?? '')
      .trim()
      .toUpperCase()
      .replace('CIRCLE', 'ELLIPSE')
      .replace('SQUARE', 'POLYGON');
  const fromType = normalize(shape.type || 'FREEHAND');
  const fromBackend = normalize(shape.backendType);
  if (!fromBackend) return fromType;
  const allowed = GEOMETRIES_FOR_SHAPE_TYPE[String(shape.type ?? '')];
  if (allowed && !allowed.has(fromBackend)) return fromType;
  return fromBackend;
}

/** Perimeter biometry on ellipses (HC, AC, …). */
function ellipsePerimeterSlot(
  categoryIdToMeasNames: Record<number, string[]>,
  categoryIdToMeasLabel: Record<number, string>,
  categoryId: number,
  measCtx: MeasGeometryContext = activeMeasGeometry
): string {
  for (const n of categoryIdToMeasNames[categoryId] ?? []) {
    if (measCtx.perimeterMeasLabels.has(n)) return `${n}:ellipse`;
  }
  const catMeas = categoryIdToMeasLabel[categoryId];
  if (catMeas && measCtx.perimeterMeasLabels.has(catMeas)) return `${catMeas}:ellipse`;
  const paired = catMeas ? measCtx.lengthToPerimeter[catMeas] : undefined;
  if (paired) return `${paired}:ellipse`;
  return 'structure:ellipse';
}

/** Length/diameter biometry on lines (BPD, FL, bladder diameter, SDP, …). */
function lineDiameterSlot(
  categoryIdToMeasNames: Record<number, string[]>,
  categoryIdToMeasLabel: Record<number, string>,
  categoryId: number,
  measCtx: MeasGeometryContext = activeMeasGeometry
): string {
  for (const n of categoryIdToMeasNames[categoryId] ?? []) {
    if (!measCtx.perimeterMeasLabels.has(n)) return `${n}:line`;
  }
  const catMeas = categoryIdToMeasLabel[categoryId];
  if (catMeas && !measCtx.perimeterMeasLabels.has(catMeas)) return `${catMeas}:line`;
  const paired = catMeas ? measCtx.perimeterToLength[catMeas] : undefined;
  if (paired) return `${paired}:line`;
  return 'structure:line';
}

/**
 * Biometry slot within a category (e.g. cephalic: MEAS__BPD line + MEAS__HC ellipse;
 * abdomen: MEAS__AC ellipse only). Used to dedupe structure+measurement duplicates without
 * collapsing different measurements that share a category.
 */
export function getBiometrySlotKey(
  seg: FrameSegmentation,
  categoryId: number,
  categoryIdToMeasLabel: Record<number, string> = {},
  categoryIdToMeasNames: Record<number, string[]> = {},
  measCtx: MeasGeometryContext = activeMeasGeometry
): string {
  const meas = getMeasLabelFromSeg(seg);
  if (meas) {
    const family = normalizeGeometryFamily(seg.geometry_type, null);
    return `${meas}:${family}`;
  }

  const geo = (seg.geometry_type ?? '').toUpperCase();

  // Geometry wins over category meas_name: cephalic category meas_name is often MEAS__BPD
  // but the ellipse outline is always HC perimeter, not BPD.
  if (geo === 'ELLIPSE' || geo === 'CIRCLE') {
    return ellipsePerimeterSlot(categoryIdToMeasNames, categoryIdToMeasLabel, categoryId, measCtx);
  }
  if (geo === 'POLYLINE' || geo === 'LINE') {
    return lineDiameterSlot(categoryIdToMeasNames, categoryIdToMeasLabel, categoryId, measCtx);
  }

  return `structure:${geo || 'unknown'}:${(seg.label ?? '').toString()}`;
}

/** Key for structure-vs-measurement point dedup on load (slot + points, not category-only). */
export function getSegmentationStructureDedupeKey(
  seg: FrameSegmentation,
  measLabelToCategoryId: Record<string, number> = {},
  categoryIdToMeasLabel: Record<number, string> = {}
): string | null {
  const catId = resolveSegmentationCategoryId(seg, measLabelToCategoryId);
  if (catId == null) return null;
  const slot = getBiometrySlotKey(seg, catId, categoryIdToMeasLabel);
  return `${catId}::${slot}::${normalizePoints(seg.points)}`;
}

export function getBiometrySlotKeyFromShape(
  shape: Shape & {
    backendLabel?: string | null;
    backendType?: string | null;
    categoryId?: number | null;
  },
  categoryIdToMeasLabel: Record<number, string> = {},
  measLabelToCategoryId: Record<string, number> = {},
  labelToMeasName: Record<string, string> = {}
): string | null {
  const resolvedLabel = resolveShapeSaveLabel(
    shape as ShapeForSave,
    categoryIdToMeasLabel,
    labelToMeasName
  );
  const measFromShape = getMeasLabelFromShape(shape);
  const measKey = resolvedLabel?.startsWith('MEAS__')
    ? resolvedLabel.split(/\s+/)[0]
    : measFromShape;

  let categoryId =
    shape.categoryId ??
    resolveSegmentationCategoryId(
      { label: shape.backendLabel ?? null },
      measLabelToCategoryId
    );
  if (categoryId == null && measKey) {
    categoryId = measLabelToCategoryId[measKey];
  }

  const family = normalizeGeometryFamily(
    (shape as ShapeForSave).backendType,
    shape.type
  );

  if (measKey) {
    const prefix = categoryId != null ? `${categoryId}::` : '';
    return `${prefix}${measKey}:${family}`;
  }

  if (categoryId == null) return null;

  const geo = (shape.backendType ?? shape.type ?? '').toString().toUpperCase();
  // Signature is (categoryIdToMeasNames, categoryIdToMeasLabel, categoryId, measCtx). We
  // don't have the meas-names map here, so pass {} and the real label map + categoryId
  // (previously these were shifted by one, leaving categoryId undefined → always generic).
  if (shape.type === 'circle' || geo === 'ELLIPSE' || geo === 'CIRCLE') {
    return `${categoryId}::${ellipsePerimeterSlot({}, categoryIdToMeasLabel, categoryId)}`;
  }
  if (shape.type === 'line' || shape.type === 'ruler' || geo === 'POLYLINE' || geo === 'LINE') {
    return `${categoryId}::${lineDiameterSlot({}, categoryIdToMeasLabel, categoryId)}`;
  }

  return `${categoryId}::structure:${geo}`;
}

/** Resolve category_id from row or MEAS__* label (e.g. MEAS__AC → abdomen category). */
export function resolveSegmentationCategoryId(
  seg: { category_id?: number | null; label?: string | null },
  measLabelToCategoryId: Record<string, number> = {}
): number | undefined {
  if (seg.category_id != null) {
    const numeric = Number(seg.category_id);
    if (!isNaN(numeric)) return numeric;
  }
  const label = (seg.label ?? '').toString().trim();
  if (label.startsWith('MEAS__')) {
    const measKey = label.split(/\s+/)[0];
    const catId = measLabelToCategoryId[measKey];
    if (catId != null) return catId;
  }
  return undefined;
}

function segBelongsToCategory(
  seg: FrameSegmentation,
  categoryId: number,
  measLabelToCategoryId: Record<string, number>,
  categoryIdToMeasLabel: Record<number, string>
): boolean {
  const resolved = resolveSegmentationCategoryId(seg, measLabelToCategoryId);
  if (resolved === categoryId) return true;
  const measLabel = categoryIdToMeasLabel[categoryId];
  if (measLabel && (seg.label ?? '').toString().trim().startsWith(measLabel)) {
    return true;
  }
  return false;
}

function isRedundantSegmentationPair(
  seg: FrameSegmentation,
  savedShape: Shape,
  categoryId: number,
  categoryIdToMeasLabel: Record<number, string>,
  measLabelToCategoryId: Record<string, number>,
  labelToMeasName: Record<string, string> = {}
): boolean {
  const segFamily = normalizeGeometryFamily(seg.geometry_type, null);
  const savedFamily = normalizeGeometryFamily(
    (savedShape as ShapeForSave).backendType,
    savedShape.type
  );
  // Line diameter and ellipse outline in the same category are not duplicates.
  if (
    segFamily !== savedFamily &&
    segFamily !== 'unknown' &&
    savedFamily !== 'unknown'
  ) {
    return false;
  }

  const segMeas = getMeasLabelFromSeg(seg);
  const savedResolved = resolveShapeSaveLabel(
    savedShape as ShapeForSave,
    categoryIdToMeasLabel,
    labelToMeasName
  );
  const savedMeas =
    savedResolved?.startsWith('MEAS__')
      ? savedResolved.split(/\s+/)[0]
      : getMeasLabelFromShape(savedShape as any);

  if (segMeas && savedMeas && segMeas !== savedMeas) {
    return false;
  }

  const segSlot = getBiometrySlotKey(seg, categoryId, categoryIdToMeasLabel);
  const savedSlotKey = getBiometrySlotKeyFromShape(
    savedShape as any,
    categoryIdToMeasLabel,
    measLabelToCategoryId,
    labelToMeasName
  );
  const savedSlot = savedSlotKey?.split('::').slice(1).join('::') ?? savedSlotKey;
  if (savedSlot && biometrySlotsMatch(segSlot, savedSlot)) {
    return true;
  }

  return false;
}

/**
 * Collapse duplicate rows within the same biometry slot (e.g. AI structure + MEAS__AC).
 * Keeps BPD + HC on cephalic views because they use different slots.
 */
export function dedupeSegmentationsForLoad(
  segmentations: FrameSegmentation[],
  categoryIdsWithMaxTwo: Set<number>,
  measLabelToCategoryId: Record<string, number> = {},
  categoryIdToMeasLabel: Record<number, string> = {},
  categories: CategoryDef[] = []
): FrameSegmentation[] {
  const bySlot = new Map<string, FrameSegmentation[]>();
  const uncategorized: FrameSegmentation[] = [];

  for (const seg of segmentations) {
    const catId = resolveSegmentationCategoryId(seg, measLabelToCategoryId);
    if (catId == null) {
      uncategorized.push(seg);
      continue;
    }
    const slotKey = `${catId}::${getBiometrySlotKey(seg, catId, categoryIdToMeasLabel)}`;
    if (!bySlot.has(slotKey)) bySlot.set(slotKey, []);
    bySlot.get(slotKey)!.push(seg);
  }

  const kept: FrameSegmentation[] = [...uncategorized];

  for (const [slotKey, group] of bySlot) {
    if (group.length <= 1) {
      kept.push(...group);
      continue;
    }

    // Multi-instance categories (max_structures === 2, e.g. the two femurs + two
    // extremities) legitimately have more than one shape in the same biometry slot. Keep
    // every geometrically-distinct instance; only collapse exact-duplicate point rows
    // (preferring the manual one), so two distinct femurs both render instead of one.
    const catId = Number(slotKey.split('::')[0]);
    // …but only for STRUCTURES. A measurement slot holds one caliper per instance for
    // multi-instance categories (femur) and exactly one everywhere else, so two saved
    // MEAS__TIBIA_LENGTH rows collapse to one instead of rendering as a duplicate.
    const isMeasurementSlot = group.every((seg) => getMeasLabelFromSeg(seg) != null);
    const catAllowsMultipleMeas =
      measurementInstanceLimit(
        categories.find((c) => c.id === catId),
        getMeasLabelFromSeg(group[0]),
        2
      ) > 1;
    if (
      categoryIdsWithMaxTwo.has(catId) &&
      (!isMeasurementSlot || catAllowsMultipleMeas)
    ) {
      const byPoints = new Map<string, FrameSegmentation>();
      for (const seg of group) {
        const pointsKey = normalizePoints(seg.points);
        const existing = byPoints.get(pointsKey);
        if (!existing || (seg.is_manual === true && existing.is_manual !== true)) {
          byPoints.set(pointsKey, seg);
        }
      }
      kept.push(...byPoints.values());
      continue;
    }

    // Same biometry slot (e.g. user BPD + AI BPD): one shape on canvas; conflict panel uses raw API rows.
    const sorted = [...group].sort((a, b) => {
      const aManual = a.is_manual === true ? 1 : 0;
      const bManual = b.is_manual === true ? 1 : 0;
      if (aManual !== bManual) return bManual - aManual;
      const aMeas = getMeasLabelFromSeg(a) ? 1 : 0;
      const bMeas = getMeasLabelFromSeg(b) ? 1 : 0;
      if (aMeas !== bMeas) return bMeas - aMeas;
      return 0;
    });
    kept.push(sorted[0]);
  }

  return kept;
}

export function shapesShareCategory(
  a: { categoryId?: number | null; backendLabel?: string | null },
  b: { categoryId?: number | null; backendLabel?: string | null },
  measLabelToCategoryId: Record<string, number> = {}
): boolean {
  const catA =
    a.categoryId ??
    resolveSegmentationCategoryId({ label: a.backendLabel ?? null }, measLabelToCategoryId);
  const catB =
    b.categoryId ??
    resolveSegmentationCategoryId({ label: b.backendLabel ?? null }, measLabelToCategoryId);
  return catA != null && catB != null && catA === catB;
}

/** Compare biometry slots ignoring category prefix (6::MEAS__BPD:line ≡ MEAS__BPD:line). */
export function biometrySlotsMatch(
  slotA: string | null | undefined,
  slotB: string | null | undefined
): boolean {
  if (!slotA || !slotB) return false;
  if (slotA === slotB) return true;
  const core = (key: string) => {
    const idx = key.indexOf('::');
    return idx >= 0 ? key.slice(idx + 2) : key;
  };
  return core(slotA) === core(slotB);
}


/** Same biometry slot (e.g. both BPD), not merely same category (BPD vs HC). */
export function shapesShareBiometrySlot(
  a: Shape,
  b: Shape,
  categoryIdToMeasLabel: Record<number, string> = {},
  measLabelToCategoryId: Record<string, number> = {},
  labelToMeasName: Record<string, string> = {}
): boolean {
  const slotA = getBiometrySlotKeyFromShape(
    a as any,
    categoryIdToMeasLabel,
    measLabelToCategoryId,
    labelToMeasName
  );
  const slotB = getBiometrySlotKeyFromShape(
    b as any,
    categoryIdToMeasLabel,
    measLabelToCategoryId,
    labelToMeasName
  );
  return biometrySlotsMatch(slotA, slotB);
}

export function collectDuplicateCategoryDeleteIds(
  changedShapes: Shape[],
  frameSegmentations: FrameSegmentation[] | undefined,
  explicitDeleteIds: (number | string)[] = [],
  categoryIdsWithMaxTwo: Set<number> = new Set(),
  categoryIdToMeasLabel: Record<number, string> = {},
  measLabelToCategoryId: Record<string, number> = {},
  labelToMeasName: Record<string, string> = {},
  categories: CategoryDef[] = []
): string[] {
  const savedDbIds = new Set<string>();
  const savedPointsByCategory = new Map<number, Set<string>>();

  for (const shape of changedShapes) {
    const dbId = (shape as any).dbId;
    if (dbId != null) {
      savedDbIds.add(String(dbId));
    }

    let categoryId = (shape as any).categoryId;
    if (categoryId == null) {
      categoryId = resolveSegmentationCategoryId(
        { label: (shape as any).backendLabel ?? null },
        measLabelToCategoryId
      );
    }
    if (categoryId == null) continue;
    const numericCat = Number(categoryId);
    if (isNaN(numericCat)) continue;

    if (dbId != null) {
      const matchingSeg = (frameSegmentations ?? []).find(
        (seg) => String(seg.id) === String(dbId)
      );
      const pointsKey = normalizePoints(matchingSeg?.points);
      if (pointsKey) {
        if (!savedPointsByCategory.has(numericCat)) {
          savedPointsByCategory.set(numericCat, new Set());
        }
        savedPointsByCategory.get(numericCat)!.add(pointsKey);
      }
    }
  }

  const siblingIds: string[] = [];

  for (const seg of frameSegmentations ?? []) {
    if (seg.id == null) continue;

    const segId = String(seg.id);
    if (savedDbIds.has(segId)) continue;

    const matchedCategory = resolveSegmentationCategoryId(seg, measLabelToCategoryId);
    if (matchedCategory == null) continue;

    const matchedSavedShape = changedShapes.find((shape) => {
      let catId = (shape as any).categoryId;
      if (catId == null) {
        catId = resolveSegmentationCategoryId(
          { label: (shape as any).backendLabel ?? null },
          measLabelToCategoryId
        );
      }
      if (Number(catId) !== matchedCategory) return false;
      return isRedundantSegmentationPair(
        seg,
        shape,
        matchedCategory,
        categoryIdToMeasLabel,
        measLabelToCategoryId,
        labelToMeasName
      );
    });
    if (!matchedSavedShape) continue;

    const cat = categories.find((c) => c.id === matchedCategory);
    const maxAllowed =
      getEffectiveMaxStructuresForSave(cat) >= 2 ||
      categoryIdsWithMaxTwo.has(matchedCategory)
        ? 2
        : 1;

    if (maxAllowed >= 2) {
      // Multi-instance categories (femur/extremities: max_structures=2 in the SAME slot)
      // keep several distinct shapes in one slot — the two femurs are NOT duplicates of
      // each other, so never retire a sibling here. (Paired biometry reaches 2 via two
      // DIFFERENT slots — one per slot — so it is not same-slot multi-instance.)
      const isSameSlotMultiInstance =
        cat == null || !categoryHasPairedLengthAndPerimeter(cat);
      if (isSameSlotMultiInstance) {
        continue;
      }
      // Paired biometry (BPD line + HC ellipse): retire other rows in the *same slot*
      // when the user edits geometry — points change, so point-equality never matched.
      if (
        isRedundantSegmentationPair(
          seg,
          matchedSavedShape,
          matchedCategory,
          categoryIdToMeasLabel,
          measLabelToCategoryId,
          labelToMeasName
        )
      ) {
        const savedDbId = (matchedSavedShape as any).dbId;
        if (savedDbId != null && segId === String(savedDbId)) continue;
        siblingIds.push(segId);
      }
      continue;
    }

    if (maxAllowed === 1) {
      if (
        isRedundantSegmentationPair(
          seg,
          matchedSavedShape,
          matchedCategory,
          categoryIdToMeasLabel,
          measLabelToCategoryId,
          labelToMeasName
        )
      ) {
        siblingIds.push(segId);
      }
      continue;
    }
  }

  const explicit = explicitDeleteIds
    .map((id) => String(id))
    .filter((id) => id && id !== 'undefined' && id !== 'null');
  const doomed = new Set<string>([...explicit, ...siblingIds]);
  for (let grew = true; grew;) {
    grew = false;
    for (const seg of frameSegmentations ?? []) {
      const segId = seg?.id != null ? String(seg.id) : '';
      const originId = seg?.origin_id != null ? String(seg.origin_id) : '';
      if (savedDbIds.has(segId)) continue;
      if (segId && originId && doomed.has(originId) && !doomed.has(segId)) {
        doomed.add(segId);
        grew = true;
      }
    }
  }

  return Array.from(doomed);
}

export type SegmentationPayload = {
  id?: string;
  category_id?: number;
  label?: string | null;
  geometry_type: string;
  segment_type: 'structure' | 'measurement';
  points: string;
  length?: number;
  perimeter?: number;
  area?: number;
  score?: number;
  client_ref?: string;
  origin_client_ref?: string;
  origin_id?: string;
};

/** Convert canvas shapes to API segmentation payloads (shared by Views & General viewer). */
export function convertShapesToSegmentations(
  shapes: Shape[],
  categoryIdToMeasLabel: Record<number, string> = {},
  labelToMeasName: Record<string, string> = {},
  measLabelToCategoryId: Record<string, number> = {},
  categories: CategoryDef[] = [],
  measCtx: MeasGeometryContext = LEGACY_MEAS_GEOMETRY
): SegmentationPayload[] {
  return shapes.map((shape: any) => {
    const geometryType = getShapeGeometryType(shape);
    const label = resolveShapeSaveLabel(
      shape,
      categoryIdToMeasLabel,
      labelToMeasName,
      categories,
      measCtx
    );
    const segment_type = resolveShapeSegmentType(label, shape, labelToMeasName);
    const measKey = label?.startsWith('MEAS__') ? label.split(/\s+/)[0] : null;
    const category_id =
      shape.categoryId ?? (measKey ? measLabelToCategoryId[measKey] : undefined);

    let pointsString = '';
    if (shape.type === 'line' || shape.type === 'ruler') {
      if (shape.startPoint && shape.endPoint) {
        pointsString = `${shape.startPoint.x}\\${shape.startPoint.y},${shape.endPoint.x}\\${shape.endPoint.y}`;
      }
    } else if (shape.type === 'square') {
      if (shape.startPoint && shape.endPoint) {
        const minX = Math.min(shape.startPoint.x, shape.endPoint.x);
        const maxX = Math.max(shape.startPoint.x, shape.endPoint.x);
        const minY = Math.min(shape.startPoint.y, shape.endPoint.y);
        const maxY = Math.max(shape.startPoint.y, shape.endPoint.y);
        pointsString = `${minX}\\${minY},${maxX}\\${minY},${maxX}\\${maxY},${minX}\\${maxY}`;
      }
    } else if (shape.type === 'circle') {
      const { center, radiusX, radiusY } = shape;
      const rotation = shape.rotation ?? 0;
      if (center) {
        const cos = Math.cos(rotation);
        const sin = Math.sin(rotation);
        const p0 = `${center.x + radiusX * cos}\\${center.y + radiusX * sin}`;
        const p1 = `${center.x - radiusX * cos}\\${center.y - radiusX * sin}`;
        const p2 = `${center.x - radiusY * sin}\\${center.y + radiusY * cos}`;
        const p3 = `${center.x + radiusY * sin}\\${center.y - radiusY * cos}`;
        pointsString = `${p0},${p1},${p2},${p3}`;
      }
    } else if (shape.type === 'freehand' && Array.isArray(shape.points)) {
      pointsString = shape.points.map((p: any) => `${p.x}\\${p.y}`).join(',');
    }

    let lengthPx: number | undefined;
    let perimeterPx: number | undefined;
    let areaPx: number | undefined;

    if (shape.type === 'line' || shape.type === 'ruler') {
      lengthPx = shape.calculatedValuePixels ?? shape.calculatedValue;
      if (lengthPx == null && shape.startPoint && shape.endPoint) {
        lengthPx = calculateLineLength(shape.startPoint, shape.endPoint);
      }
    } else if (shape.type === 'circle' && shape.center) {
      perimeterPx = shape.calculatedValuePixels ?? shape.calculatedValue;
      if (perimeterPx == null) {
        perimeterPx = calculateEllipsePerimeter(shape.radiusX, shape.radiusY);
      }
      areaPx = shape.area;
      if (areaPx == null) {
        areaPx = calculateEllipseArea(shape.radiusX, shape.radiusY);
      }
      // Length-based diameter measures (bladder, BPD line on ellipse tool, etc.)
      if (measKey && !measCtx.perimeterMeasLabels.has(measKey)) {
        lengthPx = shape.length ?? shape.calculatedValuePixels ?? shape.calculatedValue;
        if (lengthPx == null) {
          lengthPx = 2 * Math.max(shape.radiusX ?? 0, shape.radiusY ?? 0);
        }
      }
    } else if (shape.type === 'square' && shape.startPoint && shape.endPoint) {
      const w = Math.abs(shape.endPoint.x - shape.startPoint.x);
      const h = Math.abs(shape.endPoint.y - shape.startPoint.y);
      perimeterPx = 2 * (w + h);
      areaPx = shape.calculatedValuePixels ?? shape.calculatedValue;
    } else if (shape.type === 'freehand') {
      perimeterPx = shape.calculatedValuePixels ?? shape.calculatedValue;
      if (perimeterPx == null && Array.isArray(shape.points)) {
        perimeterPx = calculatePolylineLength(shape.points);
      }
    }

    return {
      id: shape.dbId != null ? String(shape.dbId) : undefined,
      category_id: category_id != null ? category_id : undefined,
      label: label != null ? label : null,
      geometry_type: geometryType,
      segment_type,
      points: pointsString,
      length: lengthPx,
      perimeter: perimeterPx,
      area: areaPx ?? shape.area,
      score: shape.score ?? 1,
      client_ref: shape.id != null ? String(shape.id) : undefined,
      origin_client_ref:
        shape.derivedFromShapeId != null ? String(shape.derivedFromShapeId) : undefined,
      origin_id: shape.originDbId != null ? String(shape.originDbId) : undefined,
    };
  });
}
